import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpErrorResponse, HttpContextToken } from '@angular/common/http';
import { isUnavailableOptionalRead } from '../utils/optional-read.utils';
import { inject } from '@angular/core';
import { Observable, retry, throwError, timer } from 'rxjs';
import { LoggerService } from '@core/services/logger.service';

/**
 * GAP-FE-033 · Retry interceptor with exponential backoff + jitter.
 *
 * The platform's HTTP chain had no retry primitive — transient 503s or
 * network blips surfaced as hard errors on quote / position / kill RPCs.
 * This interceptor retries idempotent requests (`GET` and `HEAD`) on:
 *   - 5xx responses (server-side transient failure)
 *   - HttpErrorResponse with status 0 (network failure / CORS / DNS)
 *
 * Mutating verbs (`POST`, `PUT`, `PATCH`, `DELETE`) are NEVER retried
 * here because the caller cannot prove idempotency at the interceptor
 * layer. Callers that know their mutation is idempotent should retry
 * at the call site with an explicit idempotency-key.
 *
 * Backoff schedule (3 attempts max after the original):
 *   attempt 1: 200ms + jitter
 *   attempt 2: 500ms + jitter
 *   attempt 3: 1500ms + jitter
 * Jitter is ±20% on each delay to avoid thundering-herd retry storms.
 *
 * Register in `app.config.ts` AFTER (inner of) `errorInterceptor` so retries are
 * exhausted before errorInterceptor reports/toasts, and each transient failure
 * reaches Sentry exactly once instead of once per attempt (SENTRY-NOISE-CLUSTER).
 */
/** Reads with an explicit retry action can opt out of automatic transport retries. */
export const SKIP_HTTP_RETRY = new HttpContextToken<boolean>(() => false);

const RETRY_DELAYS_MS = [200, 500, 1500];
const RETRY_METHODS = new Set(['GET', 'HEAD']);

function jitter(base: number): number {
  const variance = base * 0.2;
  return base + (Math.random() * 2 - 1) * variance;
}

function isRetryable(error: unknown): boolean {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 0) {
      // Network failure / opaque CORS — treat as transient.
      return true;
    }
    if (error.status >= 500 && error.status < 600) {
      // 5xx server errors.
      return true;
    }
  }
  return false;
}

export const retryInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const logger = inject(LoggerService);

  if (req.context.get(SKIP_HTTP_RETRY) || !RETRY_METHODS.has(req.method)) {
    return next(req);
  }

  return next(req).pipe(
    retry({
      count: RETRY_DELAYS_MS.length,
      delay: (error, retryCount) => {
        if (!isRetryable(error) || (error instanceof HttpErrorResponse && isUnavailableOptionalRead(error.status, req.url, req.method))) {
          return throwError(() => error);
        }
        const baseDelay = RETRY_DELAYS_MS[retryCount - 1] ?? RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1];
        const actualDelay = Math.max(0, jitter(baseDelay));
        // SENTRY-NOISE-CLUSTER: a retry attempt is a transient, self-healing event
        // — log at debug so it does not forward to Sentry as a warning. If every
        // retry is exhausted the terminal error still surfaces via errorInterceptor.
        logger.debug('Retrying HTTP request after transient failure', {
          url: req.urlWithParams,
          method: req.method,
          attempt: retryCount,
          delayMs: Math.round(actualDelay),
          status: error instanceof HttpErrorResponse ? error.status : 'network',
        });
        return timer(actualDelay);
      },
    })
  );
};
